/**
 * 1.4.0 asset groups, stream G3: Intake's Group filter (IntakeList.tsx's filter row; ?group=<id>, sent as groupId).
 * A group picker (Series, Season) with search; empty: all groups. Deliveries of placeholders in the group or its
 * sub-groups are listed (intakeGroupFilterSql). Groups come from G1's GET /api/v1/groups?q= (useGroups). G3's file.
 */
import { useId, useRef, useState } from 'react';
import { Layers, X } from 'lucide-react';
import { useGroup, useGroups } from '../../../api/hooks/groups';
import { useDebounced } from '../../../lib/useDebounced';
import { useDismissOnOutside } from '../../../lib/useDismissOnOutside';

export function IntakeGroupFilter({ value, onChange }: { value: string | undefined; onChange: (groupId: string) => void }) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');
  const q = useDebounced(text, 250);
  const list = useGroups({ q }, open);
  const chosen = useGroup(value);
  const ref = useRef<HTMLDivElement>(null);
  const listId = useId();
  useDismissOnOutside(ref, open, () => setOpen(false));
  if (value) {
    const g = chosen.data;
    return (
      <span className="chip intake-group-chip" data-testid="intake-filter-group">
        <Layers size={12} /> {g ? `${g.type.label}: ${[...g.path.map((p) => p.title), g.title].join(' › ')}` : 'Group'}
        <button aria-label="Remove the group filter" onClick={() => onChange('')}><X size={12} /></button>
      </span>
    );
  }
  const items = list.data?.items ?? [];
  return (
    <div className="intake-group-filter" ref={ref}>
      <button type="button" className="btn ghost sm" aria-expanded={open} aria-controls={listId} onClick={() => setOpen(!open)} data-testid="intake-filter-group">
        <Layers size={13} /> Group…
      </button>
      {open && (
        <div className="intake-group-popover" id={listId} role="dialog" aria-label="Filter by group">
          <input className="input" autoFocus placeholder="Search series and seasons" value={text} onChange={(e) => setText(e.target.value)} aria-label="Search groups" />
          <ul role="listbox" aria-label="Groups">
            {list.isPending ? <li className="faint">Loading…</li>
              : list.isError ? <li className="faint">Groups could not be loaded.</li>
              : items.length === 0 ? <li className="faint">No group matches.</li>
              : items.map((g) => (
                <li key={g.id}>
                  <button type="button" role="option" aria-selected={false} className="intake-group-option" onClick={() => { onChange(g.id); setOpen(false); setText(''); }}>
                    <span className="pill">{g.type.label}</span> {g.title}{g.number !== null && g.type.numberLabel ? <span className="faint"> · {g.number}</span> : null}
                  </button>
                </li>
              ))}
          </ul>
        </div>
      )}
    </div>
  );
}
